/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { makeAutoObservable } from "mobx"

import type { AggregatedThreatOverview, ApplicationType, ThreatOverview } from "./application-type-types"

export class ApplicationTypeStore {
    // all app types are for Administration purposes only!
    allAppTypes: Map<number, ApplicationType> = new Map()
    // user app types are for Dashboard / Applications part for the user with basic role.
    userAppTypes: Map<number, ApplicationType> = new Map()

    // Version each application's icon URL so the browser fetches a newly uploaded
    // icon instead of serving the previous image from the one-day server cache.
    iconVersions: Map<number, number> = new Map()

    threats: Map<number, ThreatOverview> = new Map()
    // Having appTypeIds in aggregatedThreatOverview feels like a workaround,
    // but it would be non-trivial to fetch current state from session store (since it lives far away).
    // It is also challenging to synchronize, since the overview responses are asynchronous and might
    // arrive in different order and at different times.
    aggregatedThreatOverview: AggregatedThreatOverview = { appTypeIds: [], insecure: 0, active: 0, outdated: 0 }

    constructor() {
        makeAutoObservable(this, {
            recalculateAggregatedThreats: true,
            setAllAppTypes: true,
            setAppType: true,
            setThreats: true,
            setUserAppTypes: true,
            updateAggregatedThreats: true,
            updateIconVersion: true
        })
    }

    get allAppTypesArray(): ApplicationType[] {
        return Array.from(this.allAppTypes.values())
    }

    setAllAppTypes(appTypes: ApplicationType[]): void {
        this.allAppTypes = new Map(appTypes.map(keyVal))
    }

    get userAppTypesArray(): ApplicationType[] {
        return Array.from(this.userAppTypes.values())
    }

    setUserAppTypes(appTypes: ApplicationType[]): void {
        this.userAppTypes = new Map(appTypes.map(keyVal))
    }

    // Update the cached record without refetching the full list. Only update
    // `userAppTypes` when it already contains the record, since that map must
    // remain limited to app types assigned to the current user.
    setAppType(appType: ApplicationType): void {
        const [id, value] = keyVal(appType)

        this.allAppTypes.set(id, value)

        if (this.userAppTypes.has(id)) {
            this.userAppTypes.set(id, value)
        }
    }

    updateIconVersion(appTypeId: number): void {
        this.iconVersions.set(appTypeId, (this.iconVersions.get(appTypeId) ?? 0) + 1)
    }

    setThreats(appTypeId: number, stats: ThreatOverview): void {
        // TODO: Randomized threats used for testing
        // const rand1 = Math.floor(Math.random() * (200 - 10 + 1)) + 10;
        // const rand2 = Math.floor(Math.random() * (200 - 10 + 1)) + 10;
        // const rand3 = Math.floor(Math.random() * (200 - 10 + 1)) + 10;
        // const rand4 = Math.floor(Math.random() * (200 - 10 + 1)) + 10;
        //
        // const alteredStats: ThreatOverview = {
        //     ...stats,
        //     outdatedAndroid: rand1,
        //     outdatedIos: rand2,
        //     insecure: rand3,
        //     active: rand4
        // }
        // this.threats.set(appTypeId, alteredStats);
        this.threats.set(appTypeId, stats)
        this.updateAggregatedThreats(appTypeId)
    }

    updateAggregatedThreats(appTypeId: number): void {
        if (!this.aggregatedThreatOverview.appTypeIds.includes(appTypeId)) return
        // if app type id is in aggregated threat overview, recalculate it
        this.recalculateAggregatedThreats(this.aggregatedThreatOverview.appTypeIds)
    }

    recalculateAggregatedThreats(appTypeIds: number[]): void {
        const aggregated: AggregatedThreatOverview = { appTypeIds: appTypeIds, insecure: 0, outdated: 0, active: 0 }
        for (const id of appTypeIds) {
            const threat = this.threats.get(id)
            if (threat) {
                aggregated.insecure += threat.insecure ?? 0
                aggregated.active += threat.active ?? 0
                aggregated.outdated = aggregated.outdated + (threat.outdatedAndroid ?? 0) + (threat.outdatedIos ?? 0)
            }
        }
        this.aggregatedThreatOverview = aggregated
    }
}

function keyVal(appType: ApplicationType): [number, ApplicationType] {
    if (appType.id) {
        return [appType.id, appType]
    }
    throw new Error("ApplicationType id is not defined.")
}
